import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import {
  fetchPopularFromCity,
  buildAviasalesDeeplink,
  searchPricesForDates,
  type FlightOffer,
  type FlightSearchDiagnostics,
} from "./travelpayouts.server";

const iata = z.string().trim().toUpperCase().regex(/^[A-Z]{3}$/, "Código IATA inválido");

const searchSchema = z.object({
  origin: iata,
  destination: iata,
  departDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  returnDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  passengers: z.number().int().min(1).max(9).optional(),
  oneWay: z.boolean().optional(),
  direct: z.boolean().optional(),
  sorting: z.enum(["price", "route"]).optional(),
  limit: z.number().int().min(1).max(1000).optional(),
  page: z.number().int().min(1).max(50).optional(),
});

export type SearchFlightsResult = {
  offers: FlightOffer[];
  deeplink: string | null;
  diagnostics: FlightSearchDiagnostics;
};

export const searchFlights = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => searchSchema.parse(d))
  .handler(async ({ data }): Promise<SearchFlightsResult> => {
    const { offers, diagnostics } = await searchPricesForDates({
      origin: data.origin,
      destination: data.destination,
      departDate: data.departDate,
      returnDate: data.returnDate,
      oneWay: data.oneWay ?? !data.returnDate,
      direct: data.direct,
      sorting: data.sorting ?? "price",
      limit: data.limit ?? 200,
      page: data.page ?? 1,
      passengers: data.passengers ?? 1,
    });

    const deeplink = data.departDate
      ? buildAviasalesDeeplink({
          origin: data.origin,
          destination: data.destination,
          departDate: data.departDate,
          returnDate: data.oneWay ? undefined : data.returnDate,
          passengers: data.passengers ?? 1,
        })
      : null;

    return { offers, deeplink, diagnostics };
  });

const popularSchema = z.object({ origin: iata.default("MEX") });

export const popularFlights = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => popularSchema.parse(d ?? {}))
  .handler(async ({ data }): Promise<{ offers: FlightOffer[] }> => {
    const offers = await fetchPopularFromCity(data.origin);
    return { offers: offers.slice(0, 12) };
  });
